import React, { CSSProperties, FC, useCallback, useState } from 'react';
import moment from 'moment';
import { Map } from 'immutable';
import classNames from 'classnames';
import { MOMENT_FORMAT } from 'src/common/constants';
import getClassName from 'src/common/utils/getClassName';
import Avatar from 'src/common/components/Avatar';
import Icon from 'src/common/components/IconDS22';
import useOpenCloseToggle from 'src/common/hooks/useOpenCloseToggle';
import ReportMessageModal from 'src/inbox/components/ReportMessageModal';
import MessageContent from '../MessageContent';
import ContactInfoWarning from '../ContactInfoWarning';
import s from './Message.module.scss';

type MessageProps = {
  avatarStyle: CSSProperties;
  message: Map<string, any>;
  currentUser: Map<string, any>;
  showDate: boolean;
  threadId: number;
};

const Message: FC<MessageProps> = ({
  avatarStyle,
  message,
  currentUser,
  showDate = false,
  threadId,
}) => {
  const timeStamp = moment(message.get('date_created')).format(`D MMM Y, ${MOMENT_FORMAT.TIME}`);
  const time = moment(message.get('date_created')).format(MOMENT_FORMAT.TIME);
  const messageUser = message.get('user');
  const isOwn = currentUser.get('id') === messageUser.get('id');
  const ownModifier = isOwn ? 'own' : '';
  const messageContent = (message.get('content') || '').trim();
  const showContactInfoWarning = message.get('was_obfuscated', false);
  const showReportMessageButton = !isOwn && message.get('subject') === 'Message';
  const [reportButtonDisabled, setReportButtonDisabled] = useState(false);

  const {
    isOpen: isReportMessageModalOpen,
    open: openReportMessageModal,
    close: closeReportMessageModal,
  } = useOpenCloseToggle(false);

  const disableReportButton = useCallback(() => {
    setReportButtonDisabled(true);
  }, []);

  return (
    <li className={getClassName(s, 'root', classNames({ showDate }))}>
      {showDate && (
        <h2 className={s.subject}>
          {`${message.get('subject')}. ${timeStamp}`}
        </h2>
      )}
      <div className={getClassName(s, 'content', ownModifier)}>
        {messageContent.length > 0 && (
          <>
            <div className={s.messageContent}>
              <MessageContent
                ownModifier={ownModifier}
                time={time}
                dateTimeString={message.get('date_created')}
              >
                {messageContent}
              </MessageContent>
              {showReportMessageButton && (
                <button
                  className={s.reportMessageButton}
                  type="button"
                  onClick={openReportMessageModal}
                  disabled={reportButtonDisabled}
                >
                  Report Message
                  <Icon id="paper-plane" size="s" />
                </button>
              )}
            </div>
            <Avatar
              user={messageUser.toJS()}
              classNameModifier={`message ${ownModifier}`}
              style={avatarStyle}
            />
          </>
        )}
      </div>
      {showContactInfoWarning
        && <ContactInfoWarning classNameModifier="messages" />}
      <ReportMessageModal
        isOpen={isReportMessageModalOpen}
        closeModal={closeReportMessageModal}
        threadId={threadId}
        messageId={message.get('id')}
        disableReportButton={disableReportButton}
      />
    </li>
  );
};

export default Message;
